/* eslint-disable fp/no-mutation */
import { toSeconds } from 'duration-fns';

import { isWiseQuote } from './quoteFilters';
import sortByDeliverySpeedEstimate from './sortQuotesByDeliverySpeedEstimate';
import sortQuotesByReceivedAmount from './sortQuotesByReceivedAmount';
import { getVisitorId } from './tracking/getVisitorId';
import { sanitisePagePath } from './tracking/sanitisePagePath';
import { Table, TableEvent, EventNames } from './tracking/types';

function isNotEmpty(quotes) {
  return quotes && quotes.length > 1;
}

function isWiseTheCheapest(quotes = []) {
  const sortedQuotes = sortQuotesByReceivedAmount(quotes);
  return sortedQuotes[0] && isWiseQuote(sortedQuotes[0]);
}

function isWiseTheFastest(quotes = []) {
  const sortedQuotes = sortByDeliverySpeedEstimate(quotes);
  return sortedQuotes[0] && isWiseQuote(sortedQuotes[0]);
}

function mapDeliveryEstimation(deliveryEstimation) {
  const estimation = deliveryEstimation || {};
  const duration = estimation.duration || {};
  const min = duration.min ? toSeconds(duration.min) : null;
  const max = duration.max ? toSeconds(duration.max) : null;

  return {
    duration: {
      min,
      max,
    },
    providerGivesEstimate: estimation.providerGivesEstimate,
  };
}

function findCheapestProviders(quotes) {
  if (!quotes || quotes.length === 0) {
    return;
  }
  const sortedQuotes = sortQuotesByReceivedAmount(quotes);
  const highestReceivedAmount = sortedQuotes[0].receivedAmount;
  const quotesWithHighestReceivedAmount = sortedQuotes.filter(
    (quote) => quote.receivedAmount === highestReceivedAmount,
  );
  return quotesWithHighestReceivedAmount.map((quote) => quote.alias);
}

function getWiseRank(quotes) {
  if (!quotes || quotes.length === 0) {
    return null;
  }
  const rank = quotes.findIndex((q) => isWiseQuote(q));
  return rank === -1 ? null : rank + 1;
}

function deriveStandardFields(context) {
  return {
    componentType: context.componentType,
    amount: context.amount,
    sourceCurrency: context.source,
    targetCurrency: context.target,
    sourceCountry: context.sourceCountry,
    providerCountry: context.providerCountry,
    providerQuotes:
      context.quotes &&
      context.quotes.map((p) => ({
        name: p.name,
        rate: p.rate,
        fees: p.fees,
        receivedAmount: p.receivedAmount,
        deliveryEstimation: mapDeliveryEstimation(p.deliveryEstimation),
      })),
    totalProvidersCount: context.quotes && context.quotes.length,
    wiseRank: getWiseRank(context.quotes),
    whitelistedProviders: context.whitelistedProviders && context.whitelistedProviders.join(),
    cheapestProviders: context.quotes && findCheapestProviders(context.quotes),
    savingsAgainstWise: context.savingsAgainstWise,
    affiliateLink: context.affiliateLink,
    lang: context.lang,
    midMarketRate: context.midMarketRate && context.midMarketRate.rate,
    visitorId: getVisitorId(),
    isWiseTheCheapest: context.quotes && isWiseTheCheapest(context.quotes),
    pagePath: sanitisePagePath(window?.location.pathname),
  };
}

function deriveAdditionalFieldsBasedOnVariant(context) {
  if (context.componentType === Table.ProviderComparison) {
    return {
      provider: {
        name: context.provider && context.provider.name,
      },
      routes:
        context.routes &&
        context.routes.map((r) => ({
          targetCurrency: r.baseProviderQuote?.targetCurrency,
          deliveryEstimation: mapDeliveryEstimation(r.baseProviderQuote?.deliveryEstimation),
          fee: r.baseProviderQuote?.fee,
          rate: r.baseProviderQuote?.rate,
          receivedAmount: r.baseProviderQuote?.receivedAmount,
          wiseQuote: {
            deliveryEstimation: mapDeliveryEstimation(r.comparedProviderQuote?.deliveryEstimation),
            savings: r.comparedProviderQuote?.receivedAmount - r.baseProviderQuote?.receivedAmount,
          },
        })),
    };
  }

  if (context.componentType === Table.TransferFee) {
    return {
      isSpeedShown: context.showSpeed,
      providerName: context.providerName,
    };
  }

  if (context.componentType === Table.HeroV2) {
    return {
      providerName: context.providerName,
      isRowExpanded: context.isRowExpanded,
      cheapestProviders: context.cheapestProviders,
      fastestProviders: context.fastestProviders,
      isWiseTheCheapest: context.isWiseTheCheapest,
      isWiseTheFastest: context.isWiseTheFastest,
    };
  }
}

function filterNonPopulatedFields(attributes) {
  const attributesForTracking = {};
  Object.keys(attributes).forEach((key) => {
    if (attributes[key] != null) {
      attributesForTracking[key] = attributes[key];
    }
  });
  return attributesForTracking;
}

/**
 * TODO We could choose to model these events separately instead of piecing together
 * which event was fired from the props and state.
 */
export function trackRouteChange(route, context, trackEvent) {
  const source = route.sourceCurrency;
  const target = route.targetCurrency;

  const SOURCE_KEY = 'source';
  const TARGET_KEY = 'target';
  const filteredRouteChanges = filterNonPopulatedFields({
    [SOURCE_KEY]: source,
    [TARGET_KEY]: target,
  });
  const partsOfRouteChanged = Object.keys(filteredRouteChanges);

  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  let eventNames;
  if (partsOfRouteChanged.length === 2) {
    eventNames = EventNames[TableEvent.CURRENCIES_SWAPPED];
    trackingAttributes.sourceCurrency = source;
    trackingAttributes.targetCurrency = target;
  } else {
    const partOfRouteChanged = partsOfRouteChanged[0];

    if (partOfRouteChanged === SOURCE_KEY) {
      trackingAttributes.previousCurrency = context.sourceCurrency;
      trackingAttributes.targetCurrency = context.targetCurrency;
      eventNames = EventNames[TableEvent.SOURCE_CURRENCY_CHANGED];
    } else if (partOfRouteChanged === TARGET_KEY) {
      trackingAttributes.previousCurrency = context.targetCurrency;
      trackingAttributes.sourceCurrency = context.sourceCurrency;
      eventNames = EventNames[TableEvent.TARGET_CURRENCY_CHANGED];
    }

    trackingAttributes[partOfRouteChanged + 'Currency'] = filteredRouteChanges[partOfRouteChanged];
  }

  trackingAttributes.midMarketRate = route.rate;

  trackEvent(eventNames, filterNonPopulatedFields(trackingAttributes));
}

export function trackAmountChange(newAmount, previousAmount, context, trackEvent) {
  const trackingAttributes = deriveStandardFields(context);
  trackingAttributes.previousAmount = previousAmount;
  trackingAttributes.amount = newAmount;

  trackEvent(EventNames[TableEvent.AMOUNT_CHANGED], filterNonPopulatedFields(trackingAttributes));
}

/**
 * TODO Refactor - The extraction of the attributes and mapping of error types
 *
 * TODO This event isn't technically just "unsupported amount". It could be a route where we have an amount but
 * not for the passed source, target, *sourceCountry*, *providerCountry* etc...
 */

export function trackWhenError(errorType, context, trackEvent) {
  const eventNames = {
    UNSUPPORTED_AMOUNT: EventNames[TableEvent.UNSUPPORTED_AMOUNT],
    UNSUPPORTED_ROUTE: EventNames[TableEvent.UNSUPPORTED_ROUTE],
  };

  if (Object.keys(eventNames).includes(errorType)) {
    const trackingAttributes = deriveStandardFields(context);

    trackEvent(eventNames[errorType], filterNonPopulatedFields(trackingAttributes));
  }
}

export function trackClickToSendMoney(context, trackLeavePageEvent, callback = () => {}) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  trackLeavePageEvent(
    EventNames[TableEvent.SEND_MONEY_CTA_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
    callback,
  );
}

export function trackClickToSeeAvailableComparison(maxAmountCollected, context, trackEvent) {
  const trackingAttributes = deriveStandardFields(context);
  trackingAttributes.maxAmountCollected = maxAmountCollected;

  trackEvent(
    EventNames[TableEvent.SEE_AVAILABLE_COMPARISON_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackIfWiseIsNotTheCheapest(quotes, context, trackEvent) {
  if (isNotEmpty(quotes) && !isWiseTheCheapest(quotes)) {
    const updatedContext = { ...context, quotes };
    const trackingAttributes = deriveStandardFields(updatedContext);

    trackEvent(
      EventNames[TableEvent.WISE_IS_NOT_THE_CHEAPEST],
      filterNonPopulatedFields(trackingAttributes),
    );
  }
}

export function trackIfWiseIsNotTheFastest(quotes, context, trackEvent) {
  if (isNotEmpty(quotes) && !isWiseTheFastest(quotes)) {
    const updatedContext = { ...context, quotes };
    const trackingAttributes = deriveStandardFields(updatedContext);

    trackEvent(
      EventNames[TableEvent.WISE_IS_NOT_THE_FASTEST],
      filterNonPopulatedFields(trackingAttributes),
    );
  }
}

export function trackClickToSeePricingDisclaimer(
  targetCountry,
  providerAlias,
  context,
  trackEvent,
) {
  const trackingAttributes = deriveStandardFields(context);
  trackingAttributes.targetCountry = targetCountry;
  trackingAttributes.providerAlias = providerAlias;

  trackEvent(
    EventNames[TableEvent.PRICING_DISCLAIMER_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackSpeedTooltipClick(context, trackEvent) {
  const trackingAttributes = deriveStandardFields(context);

  trackEvent(
    EventNames[TableEvent.DELIVERY_ESTIMATOR_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackQuotesLoad(context, quotes, trackEvent) {
  const trackingAttributes = deriveStandardFields({ ...context, quotes });

  trackEvent(EventNames[TableEvent.QUOTES_LOADED], filterNonPopulatedFields(trackingAttributes));
}

export function trackTableViewed(context, trackEvent) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  trackEvent(EventNames[TableEvent.TABLE_VIEWED], filterNonPopulatedFields(trackingAttributes));
}

export function trackFeePopoverClicked(context, trackEvent) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  trackEvent(
    EventNames[TableEvent.FEE_BREAKDOWN_POPOVER_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackExpandRowClicked(context, trackEvent) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  trackEvent(
    EventNames[TableEvent.EXPAND_ROW_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackClickToSeeDisclaimer(context, trackEvent) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
    ...deriveAdditionalFieldsBasedOnVariant(context),
  };

  trackEvent(
    EventNames[TableEvent.DISCLAIMER_CLICKED],
    filterNonPopulatedFields(trackingAttributes),
  );
}

export function trackProviderClicked(providerName, context, trackEvent) {
  const trackingAttributes = {
    ...deriveStandardFields(context),
  };

  trackingAttributes.providerName = providerName;

  trackEvent(EventNames[TableEvent.PROVIDER_CLICKED], filterNonPopulatedFields(trackingAttributes));
}

export function getTrackingTriggerIndex(rowsLength) {
  const indexValue = Math.ceil(rowsLength * 0.25);
  return Math.max(0, indexValue - 1);
}
